import { Helmet } from 'react-helmet-async';
// @mui
import {
    Card,
    Stack,
    Container,
    Typography,
    TableContainer,
    CardContent,
    Button,
    CircularProgress,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    FormControl, InputLabel, InputAdornment, OutlinedInput, ButtonGroup,
} from '@mui/material';

import {useNavigate, useParams} from 'react-router-dom';

// components
import Scrollbar from '../components/scrollbar';
// sections
import { StepByStepTable } from "../sections/@dashboard/attestations";
import {GeneralLayout} from "../layouts/GeneralLayout";
import Label from "../components/label";
import TextField from "@mui/material/TextField";
import {useEffect, useState} from "react";
import {useGetNextAttestationNumberQuery, usePostCreateAttestationMutation} from "../store/attestation/attestationApi";
import PageTitle from "../components/PageTitle";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {PrintOutlined, RemoveRedEye} from "@mui/icons-material";
import {
    useLazyAcademicStipendWebReportQuery, useLazyRetakesStatisticsOverTheUniversityWebReportQuery,
    useLazySessionResultsWebReportQuery,
    useLazySessionStatisticsWebReportQuery
} from "../store/reports/reportsApi";
import copyObject from "../utils/copyObject";
import {useGetFacultiesQuery, useLazyGetGroupsByFacultyQuery} from "../store/faculty/facultyApi";
import {useGetSemestersQuery} from "../store/system/systemApi";

export default function RetakesStatisticsOverTheUniversityReportPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [dataRequest] = useLazyRetakesStatisticsOverTheUniversityWebReportQuery();
    const [data, setData] = useState([]);
    const [examsDate, setExamsDate] = useState(null);

    function formatDate(dateObj) {
        return `${dateObj.getFullYear()}-${withZero(dateObj.getMonth() + 1)}-${withZero(dateObj.getDate())}`;
    }

    function toDateString(date){
        if (date === null || date === undefined)
            return '';

        return new Date(date).toLocaleDateString("ru-RU");
    }

    function withZero(num) {
        return num < 10 ? `0${num}` : num;
    }

    const loadData = async () => {
        setIsSaving(true);

        const request = {
            examsDate
        };
        console.log(request);

        await dataRequest({data: request}).unwrap().then((fulfilled) => {
            if (fulfilled?.success) {
                setData(copyObject(fulfilled.faculties))
            } else {
                fulfilled.errors.map((item) => {
                    alert(item);
                    return 1;
                });
            }

            setIsSaving(false);
        }).catch((e) => {
            alert('Ошибка');
            setIsSaving(false);
        });
    };

    function downloadReport(fileType) {
        setIsLoading(true);
        fetch(`/Reports/RetakeStatisticsOverTheUniversityPrintableReport?date=${examsDate}&fileType=${fileType}`,
            {
                method: "GET",
                headers: {
                    "semesterid": localStorage.getItem("selectedSemesterId"),
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('token')}`
                }
            }).then(response => {
            setIsLoading(false);
            return response.blob();
        }).then(response => {
            if (response.size < 1024*10) { // отсекаем все, что ниже 10кб
                alert('Не удалось загрузить файл')
                return;
            }

            const url = window.URL.createObjectURL(
                new Blob([response]),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `Статистика пересдач.${fileType}`,
            );

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
        }).catch((e) => {
            alert('Ошибка')
        })
    }

    return (
        <GeneralLayout>
            <PageTitle title={`Статистика пересдач по университету`} />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {isSaving ? <CircularProgress /> : <></>}
                            Статистика пересдач по университету
                        </Typography>
                        
                        <ButtonGroup size={'small'} variant={'outlined'}>
                            <Button startIcon={isLoading ? <CircularProgress size={15}/> : <PrintOutlined />}
                                    disabled={isLoading}
                                    onClick={() => {downloadReport('pdf')}}>Печать PDF</Button>
                            
                            <Button startIcon={isLoading ? <CircularProgress size={15}/> : <></>}
                                    disabled={isLoading}
                                    onClick={() => {downloadReport('xlsx')}}>XLSX</Button>
                        </ButtonGroup>
                    </Stack>

                    <Card>
                        <CardContent>
                            {/* Фильтр по дате */}
                            <Box display="flex" alignItems="center" gap={2}>
                                <TextField
                                    label="Дата экзаменов"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    onChange={(event) => {setExamsDate(event.target.value);}}
                                />
                                <Button variant="contained" startIcon={<RemoveRedEye />} onClick={loadData}>
                                    Просмотр
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {data.length === 0
                    ? <Typography align={'center'}><strong>Нет данных</strong></Typography>
                    : <></>}

                    {/* Таблица */}
                    {data.sort((l,r) => l.facultyName > r.facultyName ? 1 : -1).map((facultyData, index) => (
                        <Box key={index} mb={4}>
                            <Typography variant="h6" gutterBottom>
                                {facultyData.facultyName}
                            </Typography>

                            {facultyData.courses?.sort((l,r) => l.courseName > r.courseName ? 1 : -1).map((course, courseIndex) => (
                                <Box key={courseIndex} mb={2}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {course.courseName} ({toDateString(course.fromDate)} - {toDateString(course.toDate)})
                                    </Typography>

                                    <Card>
                                        <TableContainer>
                                            <Table size={'small'}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell rowSpan={2}>Предмет</TableCell>
                                                        <TableCell rowSpan={2}>Преподаватели</TableCell>
                                                        <TableCell rowSpan={2}>Дата пересдачи по графику</TableCell>
                                                        <TableCell colSpan={3} align={'center'}>Сведения о ведомости</TableCell>
                                                        <TableCell rowSpan={2} align={'center'}>Группа</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell align={'center'}>№ ведомости</TableCell>
                                                        <TableCell align={'center'}>Дата создания</TableCell>
                                                        <TableCell align={'center'}>Дата закрытия</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {course.rows.map((exam, examIndex) => (
                                                        <TableRow>
                                                            <TableCell>{exam.disciplineName}</TableCell>
                                                            <TableCell>{exam.employees.join(', ')}</TableCell>
                                                            <TableCell>{toDateString(exam.eventDate)}</TableCell>
                                                            <TableCell align={'center'}>{exam.statementNumber ?? 'Ведомость не создана'} </TableCell>
                                                            <TableCell align={'center'}>{exam.statementCreationDate ? toDateString(exam.statementCreationDate) : '-'}</TableCell>
                                                            <TableCell align={'center'}>{exam.statementClosingDate ? toDateString(exam.statementClosingDate) : '-'}</TableCell>
                                                            <TableCell align={'center'}>{exam.groupName}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    ))}
                    
                    <br />
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
